<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useRoute } from 'vue-router';
import {
  useConversationDisplayType,
  TABLE_LAYOUT_ROUTES,
} from 'dashboard/composables/useConversationDisplayType';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import wootConstants from 'dashboard/constants/globals';

// The Intercom-style floating layout switcher: a bottom-left pill with the
// two layouts as segmented icons, the active one filled. `L` flips them.
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { conversationDisplayType } = useConversationDisplayType();
const route = useRoute();

// The table layout only exists on the unscoped chat queue and the tickets
// screen; everywhere else the pill would be a dead control, so it hides.
const isSupportedRoute = computed(
  () =>
    TABLE_LAYOUT_ROUTES.includes(route.name) || route.name === 'tickets_index'
);

const isOnTableLayout = computed(
  () => conversationDisplayType.value === wootConstants.LAYOUT_TYPES.TABLE
);

const setLayout = table => {
  updateUISettings({
    conversation_display_type: table
      ? wootConstants.LAYOUT_TYPES.TABLE
      : uiSettings.value.previously_used_conversation_display_type ||
        wootConstants.LAYOUT_TYPES.CONDENSED,
  });
};

useKeyboardEvents({
  KeyL: { action: () => setLayout(!isOnTableLayout.value) },
});
</script>

<template>
  <div
    v-if="isSupportedRoute"
    class="absolute bottom-4 ltr:left-4 rtl:right-4 z-30 flex items-center gap-1 rounded-full bg-n-solid-2 p-1 shadow-lg outline outline-1 -outline-offset-1 outline-n-weak"
    role="group"
    :aria-label="t('CONVERSATION.LAYOUT_PILL.ARIA_LABEL')"
  >
    <button
      v-tooltip.top="`${t('CONVERSATION.LAYOUT_PILL.CHAT')} · L`"
      type="button"
      data-testid="layout-pill-chat"
      :aria-pressed="!isOnTableLayout"
      :aria-label="t('CONVERSATION.LAYOUT_PILL.CHAT')"
      class="grid rounded-full size-8 place-items-center transition-colors"
      :class="
        !isOnTableLayout
          ? 'bg-n-alpha-2 text-n-brand'
          : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
      "
      @click="setLayout(false)"
    >
      <span class="i-lucide-panel-left size-4" />
    </button>
    <button
      v-tooltip.top="`${t('CONVERSATION.LAYOUT_PILL.TABLE')} · L`"
      type="button"
      data-testid="layout-pill-table"
      :aria-pressed="isOnTableLayout"
      :aria-label="t('CONVERSATION.LAYOUT_PILL.TABLE')"
      class="grid rounded-full size-8 place-items-center transition-colors"
      :class="
        isOnTableLayout
          ? 'bg-n-alpha-2 text-n-brand'
          : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
      "
      @click="setLayout(true)"
    >
      <span class="i-lucide-table-properties size-4" />
    </button>
  </div>
</template>
